import { GraduationCap, MessagesSquare, Route, Sun } from 'lucide-react';
import type { Me } from '@/lib/api/types';
import { msg } from '@/lib/i18n';
import { defineModule } from '../types';
import {
  meCoachingRoute,
  meConversationRoute,
  meHoursRoute,
  meIndexRoute,
  meMessagesRoute,
  meNotificationsRoute,
  mePasswordRoute,
  meScoresRoute,
  meTodayRoute,
  meTripsRoute,
} from './routes';

/**
 * Me (IA §2): a driver's own day, trips, scores, coaching, messages and hours — phone-first.
 * No server module of its own: it reads coaching, comms, telemetry and tacho self-service
 * endpoints, where the subject comes from the token (RULES §API-2).
 *
 * Nav: the four destinations are the driver persona's tab bar (`me`, `me_trips`, `me_coaching`,
 * `me_messages`). They show only for an account linked to a driver (`me.user.driver_id`) — a
 * fact about the account, not a role — so an admin who is not a driver does not see them, and
 * one who is also a driver does. The pages explain themselves to accounts without a driver.
 *
 * Notifications (`/me/notifications`, R-28) and Password (`/me/password`) are for everyone: how
 * I am notified (channels per kind, mutes, quiet hours, digest) and the password I sign in with.
 * They are reached from the user menu, not the nav.
 */
const isDriver = (me: Me) => !!me.user.driver_id;
export const meModule = defineModule({
  key: 'me',
  routes: [
    meIndexRoute,
    meTodayRoute,
    meTripsRoute,
    meScoresRoute,
    meCoachingRoute,
    meMessagesRoute,
    meConversationRoute,
    meHoursRoute,
    meNotificationsRoute,
    mePasswordRoute,
  ],
  nav: [
    {
      key: 'me',
      label: msg('My day'),
      description: msg('How did I drive?'),
      icon: Sun,
      to: '/me/today',
      section: 'main',
      order: 200,
      when: isDriver,
      match: ['/me/today', '/me/scores', '/me/hours'],
      children: [
        { key: 'me_today', label: msg('Today'), to: '/me/today' },
        { key: 'me_scores', label: msg('My scores'), to: '/me/scores', permission: 'insights.driver.read' },
        { key: 'me_hours', label: msg('My driving hours'), to: '/me/hours', permission: 'tacho.activity.read' },
      ],
    },
    {
      key: 'me_trips',
      label: msg('My trips'),
      icon: Route,
      to: '/me/trips',
      section: 'main',
      order: 201,
      when: isDriver,
      permission: 'trips.trip.read',
      module: 'telemetry',
    },
    {
      key: 'me_coaching',
      label: msg('Coaching'),
      icon: GraduationCap,
      to: '/me/coaching',
      section: 'main',
      order: 202,
      when: isDriver,
      permission: 'coaching.session.read',
      module: 'coaching',
    },
    {
      key: 'me_messages',
      label: msg('Messages'),
      icon: MessagesSquare,
      to: '/me/messages',
      section: 'main',
      order: 203,
      when: isDriver,
      permission: 'comms.message.read',
      module: 'comms',
    },
  ],
});
